import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import AnnouncementSection from '../../components/admin/sections/AnnouncementSection';
import VideoSection from '../../components/admin/sections/VideoSection';
import SocialLinksSection from '../../components/admin/sections/SocialLinksSection';
import TestimonialsSection from '../../components/admin/sections/TestimonialsSection';
import VenuesSection from '../../components/admin/sections/VenuesSection';
import ScheduleSection from '../../components/admin/sections/ScheduleSection';
import ChangePasswordSection from '../../components/admin/sections/ChangePasswordSection';

// ─── Icons ────────────────────────────────────────────────────────────────────

const MegaphoneIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l19-9-9 19-2-8-8-2z" />
  </svg>
);

const CalendarIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const LocationIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ChatIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);

const VideoIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const LinkIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </svg>
);

const LockIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);

const LogoutIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

// ─── Navigation config ─────────────────────────────────────────────────────────

const NAV_ITEMS = [
  { id: 'announcement',   label: 'Announcement',   Icon: MegaphoneIcon },
  { id: 'schedule',       label: 'Schedule',       Icon: CalendarIcon  },
  { id: 'venues',         label: 'Locations',      Icon: LocationIcon  },
  { id: 'testimonials',   label: 'Testimonials',   Icon: ChatIcon      },
  { id: 'video',          label: 'Video',          Icon: VideoIcon     },
  { id: 'social-links',   label: 'Social Links',   Icon: LinkIcon      },
  { id: 'change-password',label: 'Change Password',Icon: LockIcon      },
];


function renderSection(activeSection, setActiveSection) {
  switch (activeSection) {
    case 'announcement':    return <AnnouncementSection />;
    case 'schedule':        return <ScheduleSection onNavigate={setActiveSection} />;
    case 'venues':          return <VenuesSection />;
    case 'testimonials':    return <TestimonialsSection />;
    case 'video':           return <VideoSection />;
    case 'social-links':    return <SocialLinksSection />;
    case 'change-password': return <ChangePasswordSection />;
    default:                return null;
  }
}

// ─── Main component ────────────────────────────────────────────────────────────

function AdminDashboard() {
  const [activeSection, setActiveSection] = useState('announcement');
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-dark flex" style={{ backgroundColor: '#1A1A1A' }}>

      {/* ── Desktop sidebar ─────────────────────────────────────────────────── */}
      <aside
        className="hidden md:flex flex-col fixed left-0 top-0 h-screen z-20"
        style={{ width: 240, backgroundColor: '#141414', borderRight: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* Logo */}
        <div className="px-6 py-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <a href="/" target="_blank" rel="noopener noreferrer" title="View public site">
            <img
              src="/Assets/Logos/TNL-Horizontal-White&Grey.png"
              alt="Trivia Nights Live"
              className="h-9 w-auto opacity-90 hover:opacity-100 transition-opacity duration-200"
            />
          </a>
        </div>

        {/* Nav links */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV_ITEMS.map(({ id, label, Icon }) => {
            const isActive = activeSection === id;
            return (
              <button
                key={id}
                onClick={() => setActiveSection(id)}
                className="w-full flex items-center gap-3 px-6 py-3 font-poppins text-sm font-medium transition-all duration-200"
                style={{
                  color: isActive ? '#F7941D' : 'rgba(255,255,255,0.55)',
                  backgroundColor: isActive ? 'rgba(247,148,29,0.07)' : 'transparent',
                  borderRight: isActive ? '2px solid #F7941D' : '2px solid transparent',
                }}
                onMouseEnter={e => {
                  if (!isActive) {
                    e.currentTarget.style.color = '#fff';
                    e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)';
                  }
                }}
                onMouseLeave={e => {
                  if (!isActive) {
                    e.currentTarget.style.color = 'rgba(255,255,255,0.55)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Icon size={18} />
                {label}
              </button>
            );
          })}
        </nav>

        {/* Logout */}
        <div className="px-4 py-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <button
            onClick={handleSignOut}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-poppins text-sm font-medium transition-all duration-200"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            onMouseEnter={e => {
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.color = 'rgba(255,255,255,0.4)';
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <LogoutIcon size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* ── Main content area ────────────────────────────────────────────────── */}
      {/* On desktop, push right by sidebar width (240px = ml-60). Sidebar is fixed so it needs manual offset. */}
      <div className="flex-1 flex flex-col min-w-0" style={{ marginLeft: 0 }}>
        {/* Mobile top bar (logo only) */}
        <header
          className="md:hidden flex items-center justify-between px-5 py-4 sticky top-0 z-10"
          style={{ backgroundColor: '#1A1A1A', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <a href="/" target="_blank" rel="noopener noreferrer" title="View public site">
            <img
              src="/Assets/Logos/TNL-Horizontal-White&Grey.png"
              alt="Trivia Nights Live"
              className="h-7 w-auto"
            />
          </a>
          <span className="font-poppins font-semibold text-sm" style={{ color: '#F7941D' }}>
            {NAV_ITEMS.find(n => n.id === activeSection)?.label ?? ''}
          </span>
        </header>

        {/* Page content — offset by sidebar on desktop */}
        <main className="flex-1 p-6 md:p-10 pb-24 md:pb-10 md:ml-60">
          {renderSection(activeSection, setActiveSection)}
        </main>
      </div>

      {/* ── Mobile bottom nav ────────────────────────────────────────────────── */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-20 flex"
        style={{ backgroundColor: '#141414', borderTop: '1px solid rgba(255,255,255,0.08)' }}
      >
        {NAV_ITEMS.map(({ id, label, Icon }) => {
          const isActive = activeSection === id;
          return (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              title={label}
              className="flex-1 flex flex-col items-center justify-center transition-colors duration-200 relative"
              style={{
                color: isActive ? '#F7941D' : 'rgba(255,255,255,0.35)',
                paddingTop: 14,
                paddingBottom: 14,
                minHeight: 56,
              }}
            >
              {isActive && (
                <span
                  className="absolute top-0 left-1/2 -translate-x-1/2 rounded-full"
                  style={{ width: 24, height: 2, backgroundColor: '#F7941D' }}
                />
              )}
              <Icon size={22} />
            </button>
          );
        })}
        {/* Logout */}
        <button
          onClick={handleSignOut}
          title="Logout"
          className="flex-1 flex flex-col items-center justify-center transition-colors duration-200"
          style={{ color: 'rgba(255,255,255,0.35)', paddingTop: 14, paddingBottom: 14, minHeight: 56 }}
          onTouchStart={e => { e.currentTarget.style.color = '#fff'; }}
          onTouchEnd={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.35)'; }}
        >
          <LogoutIcon size={22} />
        </button>
      </nav>

    </div>
  );
}

export default AdminDashboard;
